import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  apiLoadErrorText,
  useAddFieldSiteMemberMutation,
  useCreateFieldSiteMutation,
  useGetFieldAssignableUsersQuery,
  useGetFieldSiteMembersQuery,
  useGetFieldSitesQuery,
  useGetFieldTitulsQuery,
  useSetFieldSiteTitulMutation,
  useGetProjectVolumeFactsQuery,
} from '@shared/api';
import {
  factAcceptedQty,
  factNeedsReview,
  factReconcileFlags,
  factReconcileView,
  fmtFieldDate,
  foremenSummary,
  siteReadOnly,
  siteStatusLabel,
  siteStatusTone,
  useFieldAccess,
  volumeFactStatusLabel,
  volumeFactStatusTone,
  type FieldSite,
  type FieldTitul,
  type VolumeFact,
} from '@entities/field';
import { useAgentPermission } from '@entities/user';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { Combobox, type ComboboxOption } from '@shared/ui/Combobox';
import { ProjectTitulsPanel } from './ProjectTitulsPanel';
import { FieldRdImportButton } from '@pages/Field/FieldRdImportButton';
import { titulLabel } from '@entities/field/tituls';

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(5)};
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.h3`
  margin: 0;
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

const Sites = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const SiteBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing(3)};
`;

const SiteHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const SiteName = styled(Link)`
  font-weight: 700;
  color: inherit;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SiteMeta = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  line-height: 1.5;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 2fr 1fr 2fr auto;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: end;
  margin-top: ${({ theme }) => theme.spacing(3)};

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Select = styled.select`
  padding: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.surface};
  color: inherit;
  max-width: 100%;
`;

const FieldLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AssignBox = styled.div`
  flex: 1;
  min-width: 220px;
`;

const ErrorNote = styled.div.attrs({ role: 'alert' })`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const OkNote = styled.div`
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Chip = styled.button<{ $active: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  background: transparent;
  cursor: pointer;
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: 52vh;
  margin-top: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
  }
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-weight: 600;
    white-space: nowrap;
  }
  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const SectionTitle = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

/** Фильтры реестра фактов: что требует действия — первым. Эскалация (прораб не
 *  согласен с правкой ПТО или не подтвердил её в срок) ждёт решения ПТО — отдельно
 *  от правок, которые ждут прораба (КW2). */
const FACT_FILTERS: ReadonlyArray<{
  key: string;
  label: string;
  statuses: readonly string[] | null;
}> = [
  {
    key: 'todo',
    label: 'требуют действия',
    statuses: ['declared', 'corrected_pending_foreman', 'escalated'],
  },
  { key: 'declared', label: 'на сверке', statuses: ['declared'] },
  { key: 'foreman', label: 'ждут прораба', statuses: ['corrected_pending_foreman'] },
  { key: 'escalated', label: 'эскалация', statuses: ['escalated'] },
  { key: 'accepted', label: 'принято', statuses: ['accepted'] },
  { key: 'rejected', label: 'возвращено', statuses: ['rejected'] },
  { key: 'all', label: 'все', statuses: null },
];

/** Сколько строк реестра фактов показывать до «Показать все». */
const FACTS_PAGE = 50;

function fmtQty(v: number | null): string {
  return v === null ? '—' : String(Math.round(v * 1000) / 1000);
}

/** Роль кандидата в прорабы — подписью в выборе (прочие роли не показываем). */
function assignableRoleLabel(role: string): string[] {
  switch (role) {
    case 'foreman':
      return ['прораб'];
    case 'site_chief':
      return ['начальник участка'];
    case 'field_specialist_plus':
      return ['Прораб+'];
    default:
      return [];
  }
}

/**
 * Блок «Прораб на объекте» в карточке проекта ОСНОВА.Инженер: площадки проекта в
 * ОСНОВА.Прораб, подключение объекта (площадка → прораб → конструктив из РД) и
 * реестр фактов объёмов, которые прорабы заявили по проекту, со статусами
 * сверки. Площадку заводит сквозной доступ Прораба, прораба назначает и
 * конструктив из РД добавляет проверяющий ПТО проекта ([`useFieldAccess`]).
 * Без доступа к агенту Прораба блока нет: проект ПТО — прежде всего РД. Проект
 * в корзине — площадки только для чтения: ни подключения, ни назначения.
 */
export function FieldOnSitePanel({
  projectId,
  projectName,
  projectDeleted = false,
}: {
  projectId: string;
  projectName: string | undefined;
  /** Проект помечен на удаление: сервер не заводит площадки и не пишет в них. */
  projectDeleted?: boolean;
}) {
  const field = useAgentPermission('field');
  const access = useFieldAccess(projectId);
  const reviewer = access.reviewer;
  const skip = !projectId || !field.can_read;
  const sitesQ = useGetFieldSitesQuery({ projectId }, { skip });
  const factsQ = useGetProjectVolumeFactsQuery(projectId, { skip });
  const titulsQ = useGetFieldTitulsQuery(projectId, { skip });
  const tituls = (titulsQ.currentData?.tituls ?? []).filter((t) => t.project_id === projectId);
  const [showCreate, setShowCreate] = useState(false);
  const [createdNote, setCreatedNote] = useState<string | null>(null);
  // Реестр фактов свёрнут: страница проекта — прежде всего РД, сводка видна в шапке.
  const [showFacts, setShowFacts] = useState(false);
  const [filter, setFilter] = useState('todo');
  const [factsLimit, setFactsLimit] = useState(FACTS_PAGE);

  // Фильтр по проекту — и на клиенте: без сквозной роли сервер отдаёт площадки
  // пользователя, и `project_id` мог не учитываться.
  const sites = useMemo(
    () => (sitesQ.currentData?.sites ?? []).filter((s) => s.project_id === projectId),
    [sitesQ.currentData, projectId],
  );
  const facts = useMemo(() => factsQ.data?.facts ?? [], [factsQ.data]);
  const siteName = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);
  const counts = useMemo(() => {
    const c = new Map<string, number>();
    for (const f of FACT_FILTERS) {
      c.set(
        f.key,
        f.statuses ? facts.filter((x) => f.statuses?.includes(x.status)).length : facts.length,
      );
    }
    return c;
  }, [facts]);
  const active = FACT_FILTERS.find((f) => f.key === filter) ?? FACT_FILTERS[0];
  const shown = useMemo(
    () =>
      (active.statuses ? facts.filter((x) => active.statuses?.includes(x.status)) : facts)
        .slice()
        .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [facts, active],
  );

  const factsDenied = apiErrorStatus(factsQ.error) === 403;
  if (!field.can_read) return null;
  // Новую площадку заводит сквозной доступ Прораба или проверяющий ПТО этого
  // проекта (`create_site`); пустой проект — сразу форма, иначе — по кнопке.
  // Проект в корзине — площадку к нему не завести (сервер: «проект не найден»).
  const canCreate = (access.global || reviewer) && !projectDeleted;
  const createOpen = canCreate && (showCreate || (sites.length === 0 && sitesQ.isSuccess));

  return (
    <Panel>
      <Head>
        <Title>Прораб на объекте</Title>
        {facts.length > 0 && (
          <Summary>
            <Badge $tone="info">на сверке: {counts.get('declared') ?? 0}</Badge>
            <Badge $tone="warning">ждут прораба: {counts.get('foreman') ?? 0}</Badge>
            {(counts.get('escalated') ?? 0) > 0 && (
              <Badge
                $tone="danger"
                title="Прораб не согласен с правкой ПТО или не подтвердил её в срок — нужно решение ПТО в карточке факта"
              >
                эскалация: {counts.get('escalated')}
              </Badge>
            )}
            <Badge $tone="success">принято: {counts.get('accepted') ?? 0}</Badge>
          </Summary>
        )}
      </Head>

      <ProjectTitulsPanel key={projectId} projectId={projectId} canEdit={canCreate} />

      {sitesQ.isLoading ? (
        <Muted>Загрузка площадок…</Muted>
      ) : sitesQ.isError ? (
        <ErrorNote>
          {apiLoadErrorText(
            sitesQ.error,
            'Не удалось загрузить площадки проекта.',
            'Нет доступа к площадкам Прораба этого проекта.',
          )}
        </ErrorNote>
      ) : sites.length === 0 ? (
        <Muted>
          Объект не подключён к Прорабу: площадок нет, прорабы не могут сдавать рапорты по проекту.
          {projectDeleted
            ? ' Проект в корзине — сначала восстановите его.'
            : !canCreate && ' Подключает администратор или инженер ПТО команды проекта.'}
        </Muted>
      ) : (
        <Sites>
          {sites.map((s) => (
            <SiteRow key={s.id} site={s} reviewer={reviewer} tituls={tituls} />
          ))}
        </Sites>
      )}

      {createOpen ? (
        <CreateSiteForm
          key={projectId}
          projectId={projectId}
          tituls={tituls}
          defaultName={projectName ?? ''}
          onCreated={(text) => {
            setCreatedNote(text);
            setShowCreate(false);
          }}
          onCancel={sites.length > 0 ? () => setShowCreate(false) : null}
        />
      ) : (
        canCreate &&
        sites.length > 0 && (
          <Row>
            <Button
              $variant="ghost"
              $size="sm"
              onClick={() => {
                setCreatedNote(null);
                setShowCreate(true);
              }}
            >
              + Подключить ещё площадку
            </Button>
          </Row>
        )
      )}
      {createdNote && <OkNote>{createdNote}</OkNote>}

      <SectionTitle>Факты объёмов Прораба</SectionTitle>
      {factsQ.isLoading ? (
        <Muted style={{ marginTop: 8 }}>Загрузка фактов…</Muted>
      ) : factsQ.isError ? (
        <ErrorNote>
          {factsDenied
            ? 'Нет доступа к фактам объёмов этого проекта.'
            : apiErrorText(factsQ.error, 'Не удалось загрузить факты объёмов.')}
        </ErrorNote>
      ) : facts.length === 0 ? (
        <Muted style={{ marginTop: 8 }}>
          Прорабы ещё не заявляли объёмы по проекту — факты появляются, когда прораб сдаёт рапорт.
        </Muted>
      ) : !showFacts ? (
        <Row>
          <Button $variant="secondary" $size="sm" onClick={() => setShowFacts(true)}>
            Показать факты ({facts.length}
            {(counts.get('todo') ?? 0) > 0 ? `, требуют действия: ${counts.get('todo')}` : ''})
          </Button>
        </Row>
      ) : (
        <>
          <Row>
            {FACT_FILTERS.map((f) => (
              <Chip
                key={f.key}
                type="button"
                $active={filter === f.key}
                onClick={() => {
                  setFilter(f.key);
                  setFactsLimit(FACTS_PAGE);
                }}
              >
                {f.label} · {counts.get(f.key) ?? 0}
              </Chip>
            ))}
            <Button $variant="ghost" $size="sm" onClick={() => setShowFacts(false)}>
              Свернуть
            </Button>
          </Row>
          {shown.length === 0 ? (
            <Muted style={{ marginTop: 8 }}>В этом отборе фактов нет.</Muted>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>Заявлен</th>
                    <th>Площадка</th>
                    <th>Наименование</th>
                    <th>Заявлено</th>
                    <th>Принято</th>
                    <th>Статус</th>
                    <th>Сверка с РД</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.slice(0, factsLimit).map((f) => (
                    <FactRow key={f.id} fact={f} siteName={siteName.get(f.site_id) ?? '—'} />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
          {shown.length > factsLimit && (
            <Row>
              <Button $variant="ghost" $size="sm" onClick={() => setFactsLimit(shown.length)}>
                Показать все ({shown.length})
              </Button>
            </Row>
          )}
        </>
      )}
    </Panel>
  );
}

/** Строка реестра фактов: наименование ведёт в карточку факта (сверка, привязка). */
function FactRow({ fact: f, siteName }: { fact: VolumeFact; siteName: string }) {
  const rv = factReconcileView(f.reconcile, { elementBound: !!f.element_id });
  return (
    <tr>
      <td>{fmtFieldDate(f.created_at)}</td>
      <td>{siteName}</td>
      <td>
        <Link to={`/agents/field/${f.site_id}/volume-facts/${f.id}`}>
          {f.work_item_code ? `${f.work_item_code} — ${f.name}` : f.name}
        </Link>
        {/* «Нужна проверка» — только пока по факту нет решения ПТО. */}
        {factNeedsReview(f) && (
          <>
            {' '}
            <Badge $tone="warning">нужна проверка</Badge>
          </>
        )}
        {factReconcileFlags(f.reconcile, f.status).map((fl) => (
          <span key={fl.label}>
            {' '}
            <Badge $tone={fl.tone} title={fl.hint}>
              {fl.label}
            </Badge>
          </span>
        ))}
      </td>
      <td className="num">
        {f.qty_raw} {f.unit ?? ''}
      </td>
      <td className="num">{f.status === 'accepted' ? fmtQty(factAcceptedQty(f)) : '—'}</td>
      <td>
        <Badge $tone={volumeFactStatusTone(f.status)}>{volumeFactStatusLabel(f.status)}</Badge>
      </td>
      <td>
        <Badge $tone={rv.tone}>{rv.label}</Badge>
      </td>
    </tr>
  );
}

/** Карточка площадки проекта: прорабы + назначение прораба + конструктив из РД.
 *  Проект в корзине — площадка только для чтения (кнопок записи нет). */
function SiteRow({
  site,
  reviewer: canReview,
  tituls,
}: {
  site: FieldSite;
  reviewer: boolean;
  tituls: FieldTitul[];
}) {
  const readOnly = siteReadOnly(site);
  const reviewer = canReview && !readOnly;
  const membersQ = useGetFieldSiteMembersQuery(site.id);
  const [addMember, { isLoading: adding }] = useAddFieldSiteMemberMutation();
  const [setTitul, { isLoading: settingTitul }] = useSetFieldSiteTitulMutation();
  const [titulDraft, setTitulDraft] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [query, setQuery] = useState('');
  const [userId, setUserId] = useState('');
  const [userLabel, setUserLabel] = useState('');
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const usersQ = useGetFieldAssignableUsersQuery(
    { siteId: site.id, q: query || undefined },
    { skip: !assigning },
  );

  const members = useMemo(() => membersQ.data?.members ?? [], [membersQ.data]);
  const leads = foremenSummary(members);
  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);
  const options: ComboboxOption[] = (usersQ.data?.users ?? [])
    .filter((u) => !memberIds.has(u.id))
    .map((u) => ({
      id: u.id,
      label: u.full_name || u.email,
      sublabel: [u.email, ...u.roles.flatMap(assignableRoleLabel)].join(' · '),
    }));

  async function onAssign() {
    if (!userId) return;
    setNote(null);
    try {
      await addMember({ siteId: site.id, user_id: userId, role: 'foreman' }).unwrap();
      setNote({ ok: true, text: `Назначен прорабом площадки: ${userLabel}` });
      setUserId('');
      setUserLabel('');
      setAssigning(false);
    } catch (e) {
      setNote({ ok: false, text: apiErrorText(e, 'Не удалось назначить прораба.') });
    }
  }

  return (
    <SiteBox>
      <SiteHead>
        <SiteName to={`/agents/field/${site.id}`}>{site.name} →</SiteName>
        {readOnly ? (
          <Badge
            $tone="warning"
            title="Проект ПТО в корзине — площадка только для чтения до восстановления проекта"
          >
            только чтение
          </Badge>
        ) : (
          <Badge $tone={siteStatusTone(site.status)}>{siteStatusLabel(site.status)}</Badge>
        )}
      </SiteHead>
      <SiteMeta>
        <strong>{titulLabel(tituls, site.titul_id)}</strong>
        <br />
        {[site.code && `Шифр: ${site.code}`, site.location].filter(Boolean).join(' · ')}
        {(site.code || site.location) && <br />}
        {membersQ.isError
          ? apiLoadErrorText(
              membersQ.error,
              'Участники площадки не загрузились.',
              'Нет доступа к участникам площадки.',
            )
          : membersQ.isLoading
            ? 'Загрузка участников…'
            : leads
              ? `Ведёт площадку: ${leads}`
              : 'Прораб не назначен — рапорты сдавать некому.'}
      </SiteMeta>

      {reviewer && !site.titul_id && (
        <Row>
          <FieldLabel>
            Распределить площадку по титулу
            <Select
              aria-label={`Титул площадки ${site.name}`}
              value={titulDraft}
              onChange={(e) => setTitulDraft(e.target.value)}
            >
              <option value="">Выберите титул</option>
              {tituls.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <Button
            $size="sm"
            disabled={!titulDraft || settingTitul}
            onClick={async () => {
              setNote(null);
              try {
                await setTitul({ id: site.id, titul_id: titulDraft }).unwrap();
                setNote({ ok: true, text: 'Площадка распределена по титулу.' });
              } catch (e) {
                setNote({
                  ok: false,
                  text: apiErrorText(
                    e,
                    'Не удалось распределить площадку. Старые площадки с данными сохраняют прежние связи.',
                  ),
                });
              }
            }}
          >
            Назначить титул
          </Button>
        </Row>
      )}
      {reviewer && (
        <Row>
          {assigning ? (
            <>
              <AssignBox>
                <Combobox
                  id={`assign-${site.id}`}
                  value={userId}
                  selectedLabel={userLabel || undefined}
                  options={options}
                  onQueryChange={setQuery}
                  onChange={(o) => {
                    setUserId(o?.id ?? '');
                    setUserLabel(o?.label ?? '');
                  }}
                  loading={usersQ.isFetching}
                  placeholder="ФИО или почта прораба"
                  emptyText={
                    usersQ.isError
                      ? apiErrorText(usersQ.error, 'Список пользователей не загрузился.')
                      : query
                        ? 'Никого не нашлось'
                        : 'Нет пользователей с ролью прораба или начальника участка'
                  }
                />
              </AssignBox>
              <Button
                $variant="primary"
                $size="sm"
                disabled={!userId || adding}
                onClick={() => void onAssign()}
              >
                Назначить
              </Button>
              <Button
                $variant="ghost"
                $size="sm"
                onClick={() => {
                  setAssigning(false);
                  setUserId('');
                  setUserLabel('');
                }}
              >
                Отмена
              </Button>
            </>
          ) : (
            <>
              <Button $variant="secondary" $size="sm" onClick={() => setAssigning(true)}>
                Назначить прораба
              </Button>
              <FieldRdImportButton siteId={site.id} titulId={site.titul_id} />
            </>
          )}
        </Row>
      )}
      {note && (note.ok ? <OkNote>{note.text}</OkNote> : <ErrorNote>{note.text}</ErrorNote>)}
    </SiteBox>
  );
}

/** Подключить объект к Прорабу: завести площадку проекта. */
function CreateSiteForm({
  projectId,
  defaultName,
  tituls,
  onCreated,
  onCancel,
}: {
  projectId: string;
  defaultName: string;
  tituls: FieldTitul[];
  /** Площадка заведена — текст для пользователя (форма после этого скрывается). */
  onCreated: (text: string) => void;
  /** null — отменять нечего (у проекта ещё нет площадок, форма — единственное действие). */
  onCancel: (() => void) | null;
}) {
  const [createSite, { isLoading }] = useCreateFieldSiteMutation();
  const [name, setName] = useState<string | null>(null);
  const [titulId, setTitulId] = useState('');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Имя по умолчанию — имя проекта (подтягивается, пока пользователь не правил поле).
  const nameValue = name ?? defaultName;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = nameValue.trim();
    if (!titulId || !tituls.some((t) => t.id === titulId)) {
      setError('Сначала создайте и выберите титул площадки.');
      return;
    }
    if (!trimmed) {
      setError('Укажите название площадки.');
      return;
    }
    setError(null);
    try {
      const res = await createSite({
        project_id: projectId,
        titul_id: titulId,
        name: trimmed,
        code: code.trim() || undefined,
        location: location.trim() || undefined,
      }).unwrap();
      setName(null);
      setCode('');
      setLocation('');
      onCreated(
        `Площадка «${res.site.name}» подключена. Назначьте прораба и добавьте конструктив из РД.`,
      );
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось подключить объект к Прорабу.'));
    }
  }

  return (
    <>
      <Form onSubmit={onSubmit}>
        <FieldLabel>
          Титул
          <Select
            aria-label="Титул новой площадки"
            required
            value={titulId}
            onChange={(e) => setTitulId(e.target.value)}
          >
            <option value="">Выберите титул</option>
            {tituls.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
        <FieldLabel>
          Площадка / фронт работ
          <Input
            value={nameValue}
            onChange={(e) => setName(e.target.value)}
            placeholder="Фундаменты, оси 1–5"
            aria-label="Название площадки"
          />
        </FieldLabel>
        <FieldLabel>
          Шифр (опц.)
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="АБК-СБК" />
        </FieldLabel>
        <FieldLabel>
          Местоположение (опц.)
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Адрес или ориентир"
          />
        </FieldLabel>
        <Row style={{ marginTop: 0 }}>
          <Button
            type="submit"
            $variant="primary"
            disabled={isLoading || !nameValue.trim() || !titulId}
          >
            {isLoading ? 'Подключаю…' : 'Подключить объект к Прорабу'}
          </Button>
          {onCancel && (
            <Button type="button" $variant="ghost" onClick={onCancel}>
              Отмена
            </Button>
          )}
        </Row>
      </Form>
      {error && <ErrorNote>{error}</ErrorNote>}
    </>
  );
}
